/**
 * CustomerListPagedPage — 고객관리 목록 (서버 사이드 페이징 버전).
 *
 * 【역할】
 * - CustomerListPage 의 전건 조회와 달리 서버 사이드 페이징으로 대량 데이터 처리
 * - 페이지 번호/페이지 크기/정렬을 파라미터로 넘겨 부분 조회
 *
 * 【주요 기능】
 * - 검색 (고객명/등급/지역/유형 필터)
 * - 서버 페이징 (page, pageSize)
 * - 행 클릭 시 상세 보기 (읽기 전용)
 *
 * 【사용 훅】
 * - useTransaction (직접 호출 — useDataSetPaging 은 중첩 dataset 미대응)
 * - useCodes / useLabel
 *
 * 【Backend RPC serviceName】
 * - customer/searchListPaged  (action=search) — 페이징 응답 { rows, totalCount } 형태
 *
 * 【DataSet 구조】
 * - outds: ds_customer = { rows: [...], totalCount: N }  ← 중첩 dataset
 *
 * 【사용처】 router/index.tsx — `{ path: 'customer-paged' }`
 * 【포팅 원본】 vue-spring-fw/frontend/src/pages/PageCustomerListPaged.vue
 */
import { useCallback, useEffect, useState } from 'react'
import { Form, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTransaction } from '@/framework'
import { useCodes } from '@/framework/useCodes'
import { useLabel } from '@/framework/useLabel'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormSelect from '@/components/common/FormSelect'
import FormInput from '@/components/common/FormInput'

interface CustomerRow {
  customerId?: number | string
  customerName?: string
  customerType?: string
  phone?: string
  email?: string
  region?: string
  grade?: string
  address?: string
  creditLimit?: number
  useYn?: string
}

const gradeColor: Record<string, string> = {
  VIP: 'red',
  GOLD: 'orange',
  SILVER: 'blue',
  NORMAL: 'default'
}

/**
 * CustomerListPagedPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function CustomerListPagedPage() {
  const { getCodeName, getOptions, loadCodes } = useCodes()
  const { t } = useLabel()
  const { transaction } = useTransaction()
  const [searchForm] = Form.useForm()
  const [params, setParams] = useState<Record<string, unknown>>({})
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [rows, setRows] = useState<CustomerRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadCodes(['CUSTOMER_TYPE', 'CUSTOMER_GRADE', 'REGION'])
  }, [loadCodes])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await transaction({
        id: 'customer-paged',
        service: 'customer/searchListPaged',
        action: 'search',
        inds: {
          ds_search: { ...params, _page: page, _pageSize: size }
        }
      })
      if (res.success) {
        const ds =
          (res.data as { ds_customer?: { rows?: CustomerRow[]; totalCount?: number } })
            ?.ds_customer ?? {}
        setRows(Array.isArray(ds.rows) ? ds.rows : [])
        setTotal(typeof ds.totalCount === 'number' ? ds.totalCount : (ds.rows?.length ?? 0))
      }
    } finally {
      setLoading(false)
    }
  }, [transaction, params, page, size])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const fn_search = () => {
    const v = searchForm.getFieldsValue()
    setPage(1)
    setParams(v)
  }

  const fn_reset = () => {
    searchForm.resetFields()
    setPage(1)
    setParams({})
  }

  const columns: ColumnsType<CustomerRow> = [
    { title: t('COL_CUSTOMER_ID', '고객ID'), dataIndex: 'customerId', width: 110 },
    { title: t('COL_CUSTOMER_NAME', '고객명'), dataIndex: 'customerName', width: 160 },
    {
      title: t('COL_TYPE', '유형'),
      dataIndex: 'customerType',
      width: 100,
      render: (v: string) => getCodeName('CUSTOMER_TYPE', v)
    },
    { title: t('COL_PHONE', '연락처'), dataIndex: 'phone', width: 140 },
    { title: t('COL_EMAIL', '이메일'), dataIndex: 'email', width: 200 },
    {
      title: t('COL_REGION', '지역'),
      dataIndex: 'region',
      width: 100,
      render: (v: string) => getCodeName('REGION', v)
    },
    {
      title: t('COL_GRADE', '등급'),
      dataIndex: 'grade',
      width: 90,
      render: (v: string) => (
        <Tag color={gradeColor[v] ?? 'default'}>{getCodeName('CUSTOMER_GRADE', v)}</Tag>
      )
    },
    { title: t('COL_ADDRESS', '주소'), dataIndex: 'address' },
    {
      title: t('COL_CREDIT_LIMIT', '신용한도'),
      dataIndex: 'creditLimit',
      width: 120,
      align: 'right',
      render: (v?: number) => (v != null ? Number(v).toLocaleString() : '')
    }
  ]

  return (
    <AppPage
      search={{
        form: searchForm,
        onSearch: fn_search,
        onReset: fn_reset,
        loading,
        content: (
          <>
            <Form.Item name="keyword" label={t('LBL_KEYWORD', '키워드')}>
              <FormInput placeholder={t('LBL_CUSTOMER_NAME', '고객명')} style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="customerType" label={t('LBL_CUSTOMER_TYPE', '고객유형')}>
              <FormSelect
                mode="multiple"
                options={getOptions('CUSTOMER_TYPE')}
                style={{ minWidth: 180 }}
                placeholder={t('PH_ALL', '전체')}
              />
            </Form.Item>
            <Form.Item name="region" label={t('COL_REGION', '지역')}>
              <FormSelect
                mode="multiple"
                options={getOptions('REGION')}
                style={{ minWidth: 180 }}
                placeholder={t('PH_ALL', '전체')}
              />
            </Form.Item>
            <Form.Item name="grade" label={t('COL_GRADE', '등급')}>
              <FormSelect
                mode="multiple"
                options={getOptions('CUSTOMER_GRADE')}
                style={{ minWidth: 160 }}
                placeholder={t('PH_ALL', '전체')}
              />
            </Form.Item>
          </>
        )
      }}
    >
      <div style={{ marginBottom: 8, color: '#666' }}>
        총 <strong>{total.toLocaleString()}</strong>건
      </div>

      <DataTable<CustomerRow>
        rowKey={(r) => String(r.customerId ?? '')}
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{
          current: page,
          pageSize: size,
          total,
          showSizeChanger: true,
          showTotal: (t) => `총 ${t.toLocaleString()}건`,
          pageSizeOptions: [10, 20, 50, 100]
        }}
        onChange={(p) => {
          if (p.current && p.current !== page) setPage(p.current)
          if (p.pageSize && p.pageSize !== size) setSize(p.pageSize)
        }}
      />
    </AppPage>
  )
}
